import { useState, useEffect } from "react";
import {
  Collapse,
  Skeleton,
  Flex,
  Text,
  Box,
  Avatar,
  Image,
  Tag,
} from "@chakra-ui/react";
import { BsChevronDown, BsChevronUp } from "react-icons/bs";
import CardDefault from "components/Cards/CardDefault";
import { formatDateOntemHoje } from "infra/helpers/formatDateOntemHoje";
import { getTypeTag } from "./../../util/helper";
import Mensagem from "./../../../../../Historico/HistoricoMensagem/components/Mensagem";
import useDetalheMensagem from "features/mensagens/hooks/useDetalheMensagem";
import useLerMensagem from "features/mensagens/hooks/useLerMensagem";
import { MensagemOrigem } from "features/mensagens/typings";
import { Row, Col } from "antd";
import PrimaryButton from "ui/components/Button/PrimaryButton";
import colors from "core/resources/theme/colors";
import { MensagemItem } from "core/features/mensagens/typings";
import { useRegrasTenant } from "core/features/tenants/tenants.context";

interface CollapseMensagemItemProps {
  item: MensagemItem;
  onSave?: () => any;
  showTag: boolean;
  onArquivar: (id: string) => any;
  onDelete: (id: string) => any;
  origem: MensagemOrigem;
}

export default function CollapseMensagemItem({
  item,
  onSave,
  showTag,
  onArquivar,
  onDelete,
  origem,
}: CollapseMensagemItemProps) {
  const { useSubtenantsViews } = useRegrasTenant();
  const [collapsed, setCollapsed] = useState(true);
  const { mutate: lerMensagem } = useLerMensagem();
  const {
    data: mensagem,
    isLoading: isLoadingDetalhe,
    refetch: refetchDetalhe,
    isFetching,
  } = useDetalheMensagem(item.id, !collapsed);

  useEffect(() => {
    if (mensagem && item.id && !collapsed) {
      lerMensagem(item.id);
    }
  }, [mensagem, lerMensagem, item.id, collapsed]);

  return (
    <CardDefault pb={4} mb={2} overflow="visible" position="relative">
      {useSubtenantsViews && item.tenant?.nome ? (
        <Text
          fontSize="xs"
          fontWeight="bold"
          position="absolute"
          top={"4px"}
          left={"24px"}
          color={colors.black600}
        >
          {item.tenant.nome}
        </Text>
      ) : null}
      <Flex
        mb={2}
        px={0.5}
        cursor="pointer"
        onClick={() => setCollapsed((state) => !state)}
      >
        {collapsed ? (
          <Flex opacity={!item.respondida || item.novasRespostas ? "1" : ".4"}>
            <Flex w="200px" gridGap="16px" alignItems="center">
              {!item.imagemUrl ? (
                <Avatar
                  name={item.autor}
                  src={undefined}
                  w="35px"
                  h="35px"
                  size="sm"
                />
              ) : (
                <Image
                  title={item.autor}
                  src={item.imagemUrl || undefined}
                  w="35px"
                  h="35px"
                  rounded="full"
                />
              )}
              <Box>
                <Text
                  fontSize={"xs"}
                  fontWeight="semibold"
                  noOfLines={1}
                  title={item.autor}
                >
                  {item.autor || ""}
                </Text>

                <Text
                  fontSize={"xs"}
                  fontWeight={item.novaMensagem ? "bold" : "medium"}
                >
                  {showTag ? item.unidade : item.destino}
                  {item.numeroNovasMensagens && item.numeroNovasMensagens > 1
                    ? ` (${item.numeroNovasMensagens})`
                    : ""}
                </Text>
              </Box>
            </Flex>

            <Flex flexDir="column" w="610px">
              <Text fontSize={"xs"} fontWeight="bold">
                {item.assunto}
              </Text>

              <Flex>
                <Text fontWeight="light" noOfLines={1}>
                  {item.conteudoPlain}
                </Text>
              </Flex>
            </Flex>

            <Flex w="200px" justifyContent="flex-end" flexDir="row" gap="16px">
              <Flex gridGap={2} align="center">
                {item.novasRespostas ? (
                  <Tag
                    ml="auto"
                    rounded="full"
                    variant="outline"
                    colorScheme={"orange"}
                    fontSize={12}
                    py={1}
                    px={3}
                    textAlign="center"
                    w="max-content"
                    h={4}
                  >
                    Novas respostas
                  </Tag>
                ) : showTag ? (
                  <Tag
                    ml="auto"
                    rounded="full"
                    variant="outline"
                    colorScheme={item.respondida ? "green" : "red"}
                    fontSize={12}
                    h={4}
                  >
                    {getTypeTag(!!item.respondida)}
                  </Tag>
                ) : null}
              </Flex>
              <Flex fontSize="xs" color="gray.600" alignSelf="center">
                {formatDateOntemHoje(item.createdAt!)}
              </Flex>
            </Flex>
          </Flex>
        ) : (
          <Flex flex={1} justify="end" mr={4}>
            <Text fontSize={14} color={colors.primary} fontWeight="medium">
              {mensagem?.numRespostas || 0}{" "}
              {mensagem?.numRespostas === 1 ? "resposta" : "respostas"}
            </Text>
          </Flex>
        )}
        <Box ml={!collapsed && onSave ? "16px" : "auto"} my="auto">
          {collapsed ? (
            <BsChevronDown fontSize={20} />
          ) : (
            <BsChevronUp fontSize={20} />
          )}
        </Box>
      </Flex>
      <Collapse in={!collapsed}>
        {isLoadingDetalhe ? (
          <Skeleton h={100} flex={1} w="100%" />
        ) : (
          <Box as={Row} gutter={32}>
            <Box as={Col} md={20}>
              {mensagem ? (
                <Mensagem
                  mensagemId={mensagem.id}
                  showResponder={origem === MensagemOrigem.Recebida}
                  showLeituras={
                    !!mensagem.numDestinos &&
                    mensagem.numDestinos > 0 &&
                    origem !== MensagemOrigem.Excluida
                  }
                  data={mensagem.createdAt}
                  nome={`${mensagem.autor}`}
                  unidade={mensagem.unidade}
                  anexos={mensagem.anexos}
                  conteudo={mensagem.conteudo}
                  imagemUrl={mensagem.imagemUrl}
                  numRespostas={mensagem.numRespostas}
                  defaultOpenRespostas={true}
                  defaultCollapsed={false}
                  refetchPai={refetchDetalhe}
                  numDestinos={mensagem.numDestinos}
                  numDestinosLidos={mensagem.numDestinosLidos}
                  origem={origem}
                  isRefetchingPai={isFetching && !isLoadingDetalhe}
                />
              ) : null}
            </Box>
            <Box as={Col} md={2}>
              {origem === MensagemOrigem.Recebida ? (
                <PrimaryButton
                  size="xs"
                  mt="6px"
                  onClick={() => onArquivar(item.id)}
                >
                  Arquivar
                </PrimaryButton>
              ) : origem === MensagemOrigem.Arquivada ? (
                <PrimaryButton
                  size="xs"
                  mt="6px"
                  onClick={() => onArquivar(item.id)}
                >
                  Desarquivar
                </PrimaryButton>
              ) : origem === MensagemOrigem.Enviada ? (
                <PrimaryButton
                  size="xs"
                  mt="6px"
                  onClick={() => onDelete(item.id)}
                >
                  Excluir
                </PrimaryButton>
              ) : null}
            </Box>
          </Box>
        )}
      </Collapse>
    </CardDefault>
  );
}
